import Decimal from 'decimal.js';
import { compact } from 'lodash';

import { FUNCTIONAL } from '@/styles/tokens/colors';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';

import { EstateWaterfall_TransferLogItemFragment } from '../../../graphql/EstateWaterfall.generated';
import { EdgeLabelCard } from '../edgeLabelCard.component';
import { CardConfig } from './types';

interface QprtRemainderCardParams {
  sourceEntityName: string;
  targetEntityName: string;
  remainder?: EstateWaterfall_TransferLogItemFragment;
}

export function createQprtRemainderCard({
  sourceEntityName,
  targetEntityName,
  remainder,
}: QprtRemainderCardParams): CardConfig {
  const qprtName = sourceEntityName;
  const entityName = targetEntityName;

  const isHypothetical = remainder?.isHypothetical;

  let fundingAmount = (() => {
    if (
      remainder?.associatedObject?.__typename === 'Entity' &&
      remainder?.associatedObject?.subtype?.__typename === 'QPRTTrust'
    ) {
      return (
        remainder?.associatedObject?.subtype?.designerAccount?.initialValuation
          ?.valuationValue ?? null
      );
    }

    return null;
  })();

  // Get remainder value
  const remainderValue = remainder?.finalTransferAmount || new Decimal(0);

  const qprtTerm = (() => {
    const qprt = remainder?.associatedObject;
    if (!qprt) {
      return null;
    }

    if (
      qprt.__typename === 'Entity' &&
      qprt.subtype?.__typename === 'QPRTTrust'
    ) {
      return qprt.subtype.termDurationYears ?? null;
    }

    return null;
  })();

  const termEndDate = (() => {
    if (qprtTerm) {
      return new Date().getFullYear() + qprtTerm;
    }

    return null;
  })();

  return {
    element: (
      <EdgeLabelCard
        key={`qprt-remainder-${sourceEntityName}-${targetEntityName}`}
        title={
          isHypothetical ? 'Hypothetical QPRT remainder' : 'QPRT remainder'
        }
        titleSx={
          isHypothetical ? { color: FUNCTIONAL.WARNING[600] } : undefined
        }
        description={`Remaining balance from ${qprtName} distributed to ${entityName} at term end.`}
        rows={compact([
          fundingAmount
            ? {
                label: 'Funding value',
                value: formatCurrencyNoDecimals(fundingAmount),
              }
            : null,
          termEndDate
            ? {
                label: 'Term end',
                value: termEndDate.toString(),
              }
            : null,
          remainderValue
            ? {
                label: 'Remainder',
                value: formatCurrencyNoDecimals(remainderValue),
              }
            : null,
        ])}
      />
    ),
    label: 'QPRT remainder',
    value: formatCurrencyNoDecimals(remainderValue),
    variant: isHypothetical ? 'secondary' : 'primary',
  };
}
